import type { Finding } from '../../lib/types';

export const severityColor = (s: Finding['severity']) =>
  s === 'critical' ? '#b91c1c' : s === 'major' ? '#b45309' : s === 'info' ? '#1d4ed8' : '#047857';

/** Short, imperative "what to do" for a callout: the fix instruction, else the finding. */
export function calloutText(f: Finding): { title: string; body: string; suggestion: string | null } {
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
  return {
    title: clip(f.subject, 48),
    body: clip(f.fix.what || f.finding, 120),
    suggestion: f.fix.suggestedText ? clip(f.fix.suggestedText, 70) : null,
  };
}

// ---- callout layout (pure, unit-tested) --------------------------------------------------------------------------
export const GUTTER = 200; // px per side, independent of zoom so callouts stay readable
export const GAP = 8;
const lineCount = (s: string, charsPerLine: number) => Math.max(1, Math.ceil(s.length / charsPerLine));
/** Height estimate matching the callout CSS (11px text, clamped lines), so boxes never overlap. */
export function calloutHeight(f: Finding): number {
  const c = calloutText(f);
  const title = Math.min(2, lineCount(c.title, 24)) * 15;
  const body = Math.min(4, lineCount(c.body, 29)) * 14;
  const sugg = c.suggestion ? Math.min(2, lineCount(c.suggestion, 27)) * 14 + 4 : 0;
  return 14 + title + body + sugg;
}
export interface PlacedCallout {
  f: Finding;
  n: number;
  side: 'left' | 'right';
  top: number;
  height: number;
  /** anchor on the box (row coordinates, px) */
  ax: number;
  ay: number;
}
/**
 * Place one callout per pinned finding in the gutter closest to its box, ordered by the box's vertical position and
 * pushed down so callouts never overlap (a leader line connects callout and box). Row layout:
 * [left gutter GUTTER][page W][right gutter GUTTER].
 */
export function layoutCallouts(
  items: Finding[],
  numbers: Record<string, number>,
  pageW: number,
  pageH: number,
): { placed: PlacedCallout[]; height: number } {
  const withBox = items.filter((f) => f.location?.bbox);
  const sides: Record<'left' | 'right', PlacedCallout[]> = { left: [], right: [] };
  for (const f of withBox) {
    const [x, y, w, h] = f.location!.bbox!;
    const side = x + w / 2 < 0.5 ? 'left' : 'right';
    sides[side].push({
      f,
      n: numbers[f.id] ?? 0,
      side,
      top: 0,
      height: calloutHeight(f),
      ax: GUTTER + (side === 'left' ? x : x + w) * pageW,
      ay: (y + h / 2) * pageH,
    });
  }
  let height = pageH;
  for (const list of Object.values(sides)) {
    list.sort((a, b) => a.ay - b.ay || a.n - b.n);
    let floor = 0;
    for (const c of list) {
      c.top = Math.max(floor, c.ay - c.height / 2);
      floor = c.top + c.height + GAP;
    }
    // pull the stack up if it runs past the page bottom and there is room above
    const overflow = floor - GAP - pageH;
    if (overflow > 0 && list.length) {
      const shift = Math.min(overflow, list[0]!.top); // uniform shift keeps the spacing
      for (const c of list) c.top -= shift;
    }
    const last = list[list.length - 1];
    if (last) height = Math.max(height, last.top + last.height);
  }
  return { placed: [...sides.left, ...sides.right], height };
}
